/**
 * WebRTC Service - Handle audio calls with ColdFusion WebSocket signaling
 *
 * ColdFusion 2016+ WebSocket uses a channel-based system.
 * The WebSocket URL format is: ws://server:port/cfusion/ws/channel-name
 */

// WebRTC/WebSocket DISABLED - set to true to enable
const WEBRTC_ENABLED = false;

// ColdFusion WebSocket configuration
const WS_HOST = import.meta.env.VITE_WS_HOST || 'apicabaza.creago.be';
const WS_PORT = import.meta.env.VITE_WS_PORT || '8579'; // Default CF WebSocket port
const WS_CHANNEL = 'cabaza-signaling';
const WS_SECURE = import.meta.env.VITE_WS_SECURE === 'true';

class WebRTCService {
    constructor() {
        this.socket = null;
        this.peerConnection = null;
        this.localStream = null;
        this.remoteStream = null;
        this.userId = null;
        this.currentCallId = null;
        this.isConnected = false;
        this.isInitialized = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.pendingMessages = []; // Queue for messages before connection is ready
        this.callbacks = {
            onIncomingCall: null,
            onCallAccepted: null,
            onCallEnded: null,
            onCallRejected: null,
            onError: null,
            onConnectionStateChange: null
        };

        // ICE servers configuration (STUN/TURN)
        this.iceServers = {
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
                { urls: 'stun:stun2.l.google.com:19302' }
            ]
        };
    }

    /**
     * Initialize the service with user ID and callbacks
     */
    init(userId, callbacks = {}) {
        // Check if WebRTC is enabled
        if (!WEBRTC_ENABLED) {
            console.log('WebRTC service is disabled');
            return;
        }

        // Prevent multiple initializations
        if (this.isInitialized && this.userId === userId) {
            console.log('WebRTC service already initialized for this user');
            return;
        }

        this.userId = userId;
        this.callbacks = { ...this.callbacks, ...callbacks };
        this.isInitialized = true;
        this.connectWebSocket();
    }

    /**
     * Build WebSocket URL for ColdFusion
     */
    getWebSocketUrl() {
        const protocol = WS_SECURE ? 'wss' : 'ws';
        // ColdFusion WebSocket URL format
        return `${protocol}://${WS_HOST}:${WS_PORT}/cfusion/cfusion`;
    }

    /**
     * Connect to ColdFusion WebSocket server
     */
    connectWebSocket() {
        // Don't connect if already open or connecting
        if (this.socket?.readyState === WebSocket.OPEN ||
            this.socket?.readyState === WebSocket.CONNECTING) {
            console.log('WebSocket already connected or connecting');
            return;
        }

        try {
            const wsUrl = this.getWebSocketUrl();
            console.log('Connecting to ColdFusion WebSocket:', wsUrl);

            this.socket = new WebSocket(wsUrl);

            this.socket.onopen = () => {
                console.log('WebSocket connected');
                this.isConnected = true;
                this.reconnectAttempts = 0;

                // Subscribe to the signaling channel
                this.subscribeToChannel();

                // Send any pending messages
                this.flushPendingMessages();
            };

            this.socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.handleWebSocketMessage(data);
                } catch (e) {
                    console.error('Error parsing WebSocket message:', e);
                }
            };

            this.socket.onclose = (event) => {
                console.log('WebSocket disconnected:', event.code, event.reason);
                this.isConnected = false;

                // Attempt to reconnect only if we were initialized
                if (this.isInitialized && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
                    setTimeout(() => this.connectWebSocket(), delay);
                }
            };

            this.socket.onerror = (error) => {
                console.error('WebSocket error:', error);
                // Don't call callback for connection errors during initial connect
                if (this.isConnected) {
                    this.callbacks.onError?.('Connection error');
                }
            };
        } catch (error) {
            console.error('Failed to connect WebSocket:', error);
        }
    }

    /**
     * Flush pending messages queue
     */
    flushPendingMessages() {
        while (this.pendingMessages.length > 0) {
            const msg = this.pendingMessages.shift();
            this.sendSignal(msg);
        }
    }

    /**
     * Subscribe to the ColdFusion WebSocket channel
     */
    subscribeToChannel() {
        // Make sure socket is open before sending
        if (this.socket?.readyState !== WebSocket.OPEN) {
            console.warn('Cannot subscribe: WebSocket not open');
            return;
        }

        // ColdFusion WebSocket subscription message format
        const subscribeMsg = {
            type: 'subscribe',
            channel: WS_CHANNEL,
            subscriberInfo: {
                userId: this.userId
            }
        };

        try {
            this.socket.send(JSON.stringify(subscribeMsg));
            console.log('Subscribed to channel:', WS_CHANNEL);

            // Register user after subscription
            setTimeout(() => {
                this.sendSignal({
                    type: 'register',
                    userId: this.userId
                });
            }, 500);
        } catch (error) {
            console.error('Error subscribing to channel:', error);
        }
    }

    /**
     * Handle incoming ColdFusion WebSocket messages
     */
    handleWebSocketMessage(data) {
        // ColdFusion sends messages in a specific format
        if (data.type === 'response' && data.code === 0) {
            // Subscription successful
            console.log('Channel subscription confirmed');
            return;
        }

        if (data.type === 'data' && data.channel === WS_CHANNEL) {
            // Channel message - parse the actual message
            const message = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
            this.handleSignalingMessage(message);
            return;
        }

        // Direct message (might be sent with filterbyID)
        if (data.type) {
            this.handleSignalingMessage(data);
        }
    }

    /**
     * Send signaling message via ColdFusion WebSocket
     */
    sendSignal(message) {
        if (this.socket?.readyState === WebSocket.OPEN) {
            // ColdFusion WebSocket publish format
            const cfMessage = {
                type: 'publish',
                channel: WS_CHANNEL,
                data: JSON.stringify(message)
            };
            try {
                this.socket.send(JSON.stringify(cfMessage));
            } catch (error) {
                console.error('Error sending signal:', error);
                // Queue message for retry
                this.pendingMessages.push(message);
            }
        } else if (this.socket?.readyState === WebSocket.CONNECTING) {
            // Queue message until connection is ready
            console.log('WebSocket connecting, queuing message');
            this.pendingMessages.push(message);
        } else {
            console.warn('WebSocket not connected');
        }
    }

    /**
     * Handle incoming signaling messages
     */
    async handleSignalingMessage(message) {
        console.log('Signaling message:', message.type);

        switch (message.type) {
            case 'registered':
                console.log('User registered with signaling server');
                break;

            case 'incoming-call':
                this.currentCallId = message.callId;
                this.callbacks.onIncomingCall?.({
                    callId: message.callId,
                    callerId: message.callerId,
                    callerName: message.callerName
                });
                break;

            case 'call-accepted':
                await this.handleCallAccepted(message);
                break;

            case 'call-rejected':
                this.callbacks.onCallRejected?.();
                this.cleanup();
                break;

            case 'call-ended':
                this.callbacks.onCallEnded?.();
                this.cleanup();
                break;

            case 'offer':
                await this.handleOffer(message);
                break;

            case 'answer':
                await this.handleAnswer(message);
                break;

            case 'ice-candidate':
                await this.handleIceCandidate(message);
                break;

            case 'error':
                this.callbacks.onError?.(message.message);
                break;

            default:
                console.log('Unknown signaling message:', message.type);
        }
    }

    /**
     * Start an audio call to another user
     */
    async startCall(targetUserId, callerName) {
        try {
            // Get local audio stream
            this.localStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });

            // Create peer connection
            await this.createPeerConnection();

            // Add local tracks to peer connection
            this.localStream.getTracks().forEach(track => {
                this.peerConnection.addTrack(track, this.localStream);
            });

            // Create and send offer
            const offer = await this.peerConnection.createOffer();
            await this.peerConnection.setLocalDescription(offer);

            // Generate call ID
            this.currentCallId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            // Send call initiation
            this.sendSignal({
                type: 'call',
                callId: this.currentCallId,
                targetUserId,
                callerId: this.userId,
                callerName,
                offer: offer
            });

            return this.currentCallId;
        } catch (error) {
            console.error('Error starting call:', error);
            this.callbacks.onError?.(error.message);
            this.cleanup();
            throw error;
        }
    }

    /**
     * Accept an incoming call
     */
    async acceptCall(callId) {
        try {
            this.currentCallId = callId;

            // Get local audio stream
            this.localStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });

            // Send acceptance
            this.sendSignal({
                type: 'accept-call',
                callId,
                userId: this.userId
            });
        } catch (error) {
            console.error('Error accepting call:', error);
            this.callbacks.onError?.(error.message);
            this.rejectCall(callId);
        }
    }

    /**
     * Reject an incoming call
     */
    rejectCall(callId) {
        this.sendSignal({
            type: 'reject-call',
            callId,
            userId: this.userId
        });
        this.cleanup();
    }

    /**
     * End the current call
     */
    endCall() {
        if (this.currentCallId) {
            this.sendSignal({
                type: 'end-call',
                callId: this.currentCallId,
                userId: this.userId
            });
        }
        this.cleanup();
        this.callbacks.onCallEnded?.();
    }

    /**
     * Handle call accepted - create offer
     */
    async handleCallAccepted(message) {
        this.callbacks.onCallAccepted?.();
    }

    /**
     * Handle incoming offer
     */
    async handleOffer(message) {
        try {
            await this.createPeerConnection();

            // Add local tracks
            this.localStream.getTracks().forEach(track => {
                this.peerConnection.addTrack(track, this.localStream);
            });

            // Set remote description
            await this.peerConnection.setRemoteDescription(
                new RTCSessionDescription(message.offer)
            );

            // Create and send answer
            const answer = await this.peerConnection.createAnswer();
            await this.peerConnection.setLocalDescription(answer);

            this.sendSignal({
                type: 'answer',
                callId: this.currentCallId,
                userId: this.userId,
                answer: answer
            });
        } catch (error) {
            console.error('Error handling offer:', error);
            this.callbacks.onError?.(error.message);
        }
    }

    /**
     * Handle incoming answer
     */
    async handleAnswer(message) {
        try {
            await this.peerConnection.setRemoteDescription(
                new RTCSessionDescription(message.answer)
            );
        } catch (error) {
            console.error('Error handling answer:', error);
        }
    }

    /**
     * Handle incoming ICE candidate
     */
    async handleIceCandidate(message) {
        try {
            if (message.candidate && this.peerConnection) {
                await this.peerConnection.addIceCandidate(
                    new RTCIceCandidate(message.candidate)
                );
            }
        } catch (error) {
            console.error('Error handling ICE candidate:', error);
        }
    }

    /**
     * Create RTCPeerConnection
     */
    async createPeerConnection() {
        this.peerConnection = new RTCPeerConnection(this.iceServers);

        // Handle ICE candidates
        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                this.sendSignal({
                    type: 'ice-candidate',
                    callId: this.currentCallId,
                    userId: this.userId,
                    candidate: event.candidate
                });
            }
        };

        // Handle connection state changes
        this.peerConnection.onconnectionstatechange = () => {
            const state = this.peerConnection.connectionState;
            console.log('Connection state:', state);
            this.callbacks.onConnectionStateChange?.(state);

            if (state === 'disconnected' || state === 'failed') {
                this.callbacks.onCallEnded?.();
                this.cleanup();
            }
        };

        // Handle incoming tracks
        this.peerConnection.ontrack = (event) => {
            console.log('Received remote track');
            this.remoteStream = event.streams[0];

            // Create audio element for remote audio
            let remoteAudio = document.getElementById('remote-audio');
            if (!remoteAudio) {
                remoteAudio = document.createElement('audio');
                remoteAudio.id = 'remote-audio';
                remoteAudio.autoplay = true;
                document.body.appendChild(remoteAudio);
            }
            remoteAudio.srcObject = this.remoteStream;
        };
    }

    /**
     * Toggle microphone mute
     */
    toggleMute() {
        if (this.localStream) {
            const audioTrack = this.localStream.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                return !audioTrack.enabled; // Returns true if muted
            }
        }
        return false;
    }

    /**
     * Check if microphone is muted
     */
    isMuted() {
        if (this.localStream) {
            const audioTrack = this.localStream.getAudioTracks()[0];
            return audioTrack ? !audioTrack.enabled : true;
        }
        return true;
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        // Stop local stream
        if (this.localStream) {
            this.localStream.getTracks().forEach(track => track.stop());
            this.localStream = null;
        }

        // Close peer connection
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }

        // Remove remote audio element
        const remoteAudio = document.getElementById('remote-audio');
        if (remoteAudio) {
            remoteAudio.srcObject = null;
        }

        this.remoteStream = null;
        this.currentCallId = null;
    }

    /**
     * Disconnect completely
     */
    disconnect() {
        this.cleanup();
        this.isInitialized = false;
        if (this.socket) {
            // Unsubscribe from channel only if socket is open
            if (this.socket.readyState === WebSocket.OPEN) {
                try {
                    const unsubscribeMsg = {
                        type: 'unsubscribe',
                        channel: WS_CHANNEL
                    };
                    this.socket.send(JSON.stringify(unsubscribeMsg));
                } catch (error) {
                    console.error('Error unsubscribing:', error);
                }
            }

            this.socket.close();
            this.socket = null;
        }
        this.isConnected = false;
    }

    /**
     * Check if browser supports WebRTC
     */
    static isSupported() {
        return !!(
            navigator.mediaDevices?.getUserMedia &&
            window.RTCPeerConnection
        );
    }

    /**
     * Check if connected to signaling server
     */
    isSignalingConnected() {
        return this.isConnected && this.socket?.readyState === WebSocket.OPEN;
    }
}

// Singleton instance
const webrtcService = new WebRTCService();
export default webrtcService;
